import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import Modal from "../components/common/Modal";
import Input from "../components/common/Input";
import OnScreenKeyboard from "../components/common/OnScreenKeyboard";

const UiSecurityContext = createContext(null);

const STORAGE_PREFIX = "posAdmin.uiSecurity";
const UI_SECURITY_PASSWORD =
  process.env.REACT_APP_UI_SECURITY_PASSWORD?.trim() || "";

const SECTION_CONFIG = {
  users: {
    title: "Secure Access",
  },
  printers: {
    title: "Secure Access",
  },
  generalSettings: {
    title: "Secure Access",
  },
  tills: {
    title: "Secure Access",
  },
  tillSessions: {
    title: "Secure Access",
  },
  cashRefunds: {
    title: "Authorize Refunds",
  },
};

const getStorageKey = (sectionKey) => `${STORAGE_PREFIX}.${sectionKey}`;

const readInitialState = () => {
  if (typeof window === "undefined") {
    return {};
  }

  return Object.keys(SECTION_CONFIG).reduce((acc, sectionKey) => {
    acc[sectionKey] =
      window.sessionStorage.getItem(getStorageKey(sectionKey)) === "true";
    return acc;
  }, {});
};

export const UiSecurityProvider = ({ children }) => {
  const [unlockedSections, setUnlockedSections] = useState(readInitialState);
  const [showPassword, setShowPassword] = useState(false);
  const [promptState, setPromptState] = useState({
    isOpen: false,
    sectionKey: null,
    password: "",
    error: "",
    onSuccess: null,
  });

  const isProtectionEnabled = Boolean(UI_SECURITY_PASSWORD);

  const isUnlocked = useCallback(
    (sectionKey) =>
      !isProtectionEnabled || Boolean(unlockedSections?.[sectionKey]),
    [isProtectionEnabled, unlockedSections],
  );

  const closePrompt = useCallback(() => {
    setShowPassword(false);
    setPromptState({
      isOpen: false,
      sectionKey: null,
      password: "",
      error: "",
      onSuccess: null,
    });
  }, []);

  const requestAccess = useCallback(
    (sectionKey, onSuccess) => {
      if (!SECTION_CONFIG[sectionKey]) {
        onSuccess?.();
        return true;
      }

      if (!isProtectionEnabled || unlockedSections?.[sectionKey]) {
        onSuccess?.();
        return true;
      }

      setPromptState({
        isOpen: true,
        sectionKey,
        password: "",
        error: "",
        onSuccess: onSuccess || null,
      });
      setShowPassword(false);
      return false;
    },
    [isProtectionEnabled, unlockedSections],
  );

  const handlePasswordChange = useCallback((event) => {
    const { value } = event.target;
    setPromptState((current) => ({
      ...current,
      password: value,
      error: "",
    }));
  }, []);

  const handleConfirm = useCallback(() => {
    const submittedPassword = promptState.password.trim();

    if (!submittedPassword) {
      setPromptState((current) => ({
        ...current,
        error: "Password is required.",
      }));
      return;
    }

    if (submittedPassword !== UI_SECURITY_PASSWORD) {
      setPromptState((current) => ({
        ...current,
        error: "Incorrect password.",
      }));
      return;
    }

    const activeSection = promptState.sectionKey;
    const successHandler = promptState.onSuccess;

    setUnlockedSections((current) => {
      const nextState = {
        ...current,
        [activeSection]: true,
      };

      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(getStorageKey(activeSection), "true");
      }

      return nextState;
    });

    closePrompt();
    successHandler?.();
  }, [closePrompt, promptState]);

  const activeSectionConfig = promptState.sectionKey
    ? SECTION_CONFIG[promptState.sectionKey]
    : null;

  const value = useMemo(
    () => ({
      isProtectionEnabled,
      isUnlocked,
      requestAccess,
    }),
    [isProtectionEnabled, isUnlocked, requestAccess],
  );

  return (
    <UiSecurityContext.Provider value={value}>
      {children}
      <Modal
        isOpen={promptState.isOpen}
        onClose={closePrompt}
        title={activeSectionConfig?.title}
        size="xl"
        backdropClassName="bg-slate-950/45 backdrop-blur-sm"
      >
        <div className="space-y-5">
          <div className="rounded-[28px] border border-slate-200/80 bg-gradient-to-br from-slate-50 via-white to-indigo-50/60 p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)] dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
            <div className="space-y-4">
              <Input
                label="Password"
                type={showPassword ? "text" : "password"}
                value={promptState.password}
                onChange={handlePasswordChange}
                error={promptState.error}
                autoFocus
                className="h-14 rounded-2xl border-slate-300 bg-white/90 pr-14 text-lg tracking-[0.18em] shadow-sm dark:bg-slate-800"
                endAdornment={
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white/90 text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-700 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-slate-100"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M3 3l18 18M10.58 10.58A2 2 0 0013.42 13.42M9.88 5.09A9.77 9.77 0 0112 4.8c5.05 0 9.27 3.11 10.5 7.2a11.84 11.84 0 01-4.04 5.76M6.1 6.1A11.82 11.82 0 001.5 12c1.23 4.09 5.45 7.2 10.5 7.2a9.8 9.8 0 004.02-.85"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-5 w-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.8}
                          d="M2.46 12C3.73 7.94 7.95 4.8 12 4.8c4.05 0 8.27 3.14 9.54 7.2-1.27 4.06-5.49 7.2-9.54 7.2-4.05 0-8.27-3.14-9.54-7.2z"
                        />
                        <circle cx="12" cy="12" r="3" strokeWidth={1.8} />
                      </svg>
                    )}
                  </button>
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleConfirm();
                  }
                }}
              />

              <OnScreenKeyboard
                title={null}
                value={promptState.password}
                doneLabel="Unlock"
                showModeIndicator={false}
                onChange={(nextValue) =>
                  setPromptState((current) => ({
                    ...current,
                    password: nextValue,
                    error: "",
                  }))
                }
                onDone={handleConfirm}
              />
            </div>
          </div>
        </div>
      </Modal>
    </UiSecurityContext.Provider>
  );
};

export const useUiSecurity = () => {
  const context = useContext(UiSecurityContext);

  if (!context) {
    throw new Error("useUiSecurity must be used within UiSecurityProvider");
  }

  return context;
};
